import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useActiveAccount } from '@/store/active-account/active-account.store';
import { getInfiniteQueryListNextParam } from '@/utils/list';

import {
  cancelSubscription,
  createSubscriptionCheckout,
  createSubscriptionPortal,
  getAllSubscriptions,
  getBilledServiceIds,
  getReferral,
  getSubscription,
  getSubscriptionPrices,
  uncancelSubscription,
  updateSubscription,
} from './subscriptions.api';
import type {
  CancelSubscription,
  CreateCheckoutSubscription,
  Subscription,
  UpdateSubscription,
} from './subscriptions.types';

const SUBSCRIPTION_KEY = 'subscription';
const SUBSCRIPTIONS_KEY = 'subscriptions';
const SUBSCRIPTION_PRICES_KEY = 'subscription-prices';
const REFERRAL_KEY = 'referral';

/**
 * Folds the per-service reads into one lookup. Module level, so its identity is stable —
 * `useQueries` memoises on the combine function, and an inline one would rebuild the map on
 * every render. Keyed on each row's own `serviceId`, so it closes over nothing.
 *
 * Only the services that answered with a row are in it: a failed read is not "no plan", and
 * neither absence nor a `null` answer can be told from the other here — both read as the free
 * plan, which is what an unanswered service falls back to anyway.
 */
const combineSubscriptions = (results: UseQueryResult<Subscription | null>[]) => ({
  byServiceId: new Map(
    results.flatMap((result) =>
      result.data ? [[result.data.serviceId, result.data] as const] : [],
    ),
  ),
  isPending: results.some((result) => result.isPending),
  error: results.find((result) => result.error)?.error ?? null,
});

export const useInvalidateSubscriptions = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_KEY] });
    queryClient.invalidateQueries({ queryKey: [SUBSCRIPTIONS_KEY] });
  };
};

/**
 * The active service's subscription, or `null` when it has none. Keyed by service id
 * because the endpoint is scoped by the `X-Service-Id` header — switching vessels must
 * not read another service's cached row.
 *
 * `enabled: false` holds the request back for callers that only sometimes need the plan:
 * asking for a subscription the caller has no business reading is a 403, not a `null`.
 */
export const useSubscriptionQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const serviceId = useActiveAccount((state) => state.serviceId);
  return useQuery({
    queryKey: [SUBSCRIPTION_KEY, serviceId],
    queryFn: () => getSubscription(),
    enabled: enabled && !!serviceId,
  });
};

/**
 * One subscription per named service, each read on its own and keyed exactly as the active
 * service's is, so the two never hold different answers for the same vessel.
 *
 * A vessel at a time rather than `/subscriptions/all` in one request, because that endpoint
 * answers a different question — the rows the caller is *billed for*. A captain listing a
 * vessel whose owner pays would find no row of theirs for it and read the plan as free, and
 * so would an owner whose captain pays for their boat.
 */
export const useSubscriptionsQuery = (serviceIds: string[]) =>
  useQueries({
    queries: serviceIds.map((serviceId) => ({
      queryKey: [SUBSCRIPTION_KEY, serviceId],
      queryFn: () => getSubscription(serviceId),
    })),
    combine: combineSubscriptions,
  });

/**
 * Every subscription the caller is billed for, across all their services — which is not the
 * same set as the vessels whose plans they manage, so a vessel list reads each vessel's own
 * row through `useSubscriptionsQuery` instead.
 */
export const useAllSubscriptionsQuery = (limit: number) =>
  useInfiniteQuery({
    queryKey: [SUBSCRIPTIONS_KEY, 'all', limit],
    queryFn: ({ pageParam }) => getAllSubscriptions(limit, pageParam),
    initialPageParam: 1,
    getNextPageParam: getInfiniteQueryListNextParam,
  });

/**
 * Which of the caller's vessels are on *their* card.
 *
 * A vessel's own row says what the plan is and never who pays for it, so a screen that shows
 * an amount has to ask this before calling it the viewer's bill: a plan a captain bought is
 * the captain's until the owner takes it over.
 *
 * Caller-scoped, so it is keyed without a service id — the active one only satisfies the
 * `X-Service-Id` middleware. It sits under the subscriptions prefix, so a plan change discards
 * it along with the rows themselves.
 */
export const useBilledServiceIdsQuery = () => {
  const serviceId = useActiveAccount((state) => state.serviceId);
  return useQuery({
    queryKey: [SUBSCRIPTIONS_KEY, 'billed'],
    queryFn: getBilledServiceIds,
    enabled: !!serviceId,
  });
};

export const useSubscriptionPricesQuery = (lookupKeys: string[]) =>
  useQuery({
    queryKey: [SUBSCRIPTION_PRICES_KEY, lookupKeys],
    queryFn: () => getSubscriptionPrices(lookupKeys),
    enabled: lookupKeys.length > 0,
  });

/**
 * The caller's referral link and how many people have joined through it.
 *
 * Not keyed by service: referral belongs to the person, so it survives a vessel switch.
 * Read-only — the API offers no way to regenerate the link, send an invite or redeem a
 * code, so an invite list or reward tiers cannot be backed by it today.
 */
export const useReferralQuery = () =>
  useQuery({
    queryKey: [REFERRAL_KEY],
    queryFn: getReferral,
  });

/**
 * Starts a paid subscription and resolves to the Stripe checkout URL — redirect the
 * user there. The row already exists as `incomplete` at that point; it flips to active
 * only once Stripe's webhook lands, so the refetched query may still read incomplete
 * right after the user returns.
 *
 * Rejects with a 403 when the paid-service cap is reached, which wants a contact-sales
 * branch — but the thrown error carries no status, so that case currently arrives
 * indistinguishable from a validation failure.
 */
export const useSubscriptionCheckoutMutation = () => {
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: (values: CreateCheckoutSubscription) => createSubscriptionCheckout(values),
    onSuccess: invalidate,
  });
};

/**
 * Changes tier / interval / seats. Resolves to a Stripe URL when the subscription was
 * on `harbor` (the server starts a checkout instead of editing), or `null` when the
 * change was applied directly — the caller must handle both.
 *
 * Rejects with 502 when Stripe could not price the change; nothing was altered and the
 * call is safe to retry, though that is likewise not distinguishable from a 500 here.
 */
export const useUpdateSubscriptionMutation = () => {
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: UpdateSubscription }) =>
      updateSubscription(id, values),
    onSuccess: invalidate,
  });
};

/** Schedules cancellation at period end; access continues until `cancelAt`. */
export const useCancelSubscriptionMutation = () => {
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values?: CancelSubscription }) =>
      cancelSubscription(id, values),
    onSuccess: invalidate,
  });
};

export const useUncancelSubscriptionMutation = () => {
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: (id: string) => uncancelSubscription(id),
    onSuccess: invalidate,
  });
};

/** Stripe billing-portal URL — payment methods, invoices, receipts. */
export const useSubscriptionPortalMutation = () =>
  useMutation({
    mutationFn: () => createSubscriptionPortal(),
  });
